'use client';

/** @app — application layer; owned per-fork (LMS / OTT). See PLATFORM_FILES.md. */
import type { ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import type { CourseCardProps } from '../cards/CourseCard.types';
import {
  AiHighlightsBlock,
  Breadcrumb,
  CareerBlock,
  ChapterTree,
  CourseLayout,
  CourseTabs,
  CourseTitleBlock,
  FaqAccordion,
  InstructorCard,
  OutcomesList,
  PreviewVideoCard,
  PriceCard,
  RatingHistogram,
  RelatedCourses,
  ResourceList,
  ReviewCard,
  SectionShell,
  SkillChips,
  StatRow,
  type CourseDetailProps,
  type TabSpec,
} from './shared';

// CourseDetailTrustFirst — credibility-led.
//
// "Can I trust this instructor / institution to teach me well?"
//
// Above the tabs:
//   - Title block
//   - Large preview video card (left) + Instructor card lg (right)
//   - One featured review (pulled up from the reviews section)
//
// Tabs (with outcomes first since that's still what customers want
// to know about, but the trust-building hero already answered
// "is this credible?"):
//   outcomes → curriculum → all-reviews → career → faq → resources
//
// Sidebar is quieter (minimal PriceCard variant) — trust-first leads with
// social proof, not commerce.

interface TrustFirstProps extends CourseDetailProps {
  SimilarCard: ComponentType<{ course: CourseCardProps }>;
}

export function CourseDetailTrustFirst({
  tenantSlug,
  course,
  related,
  SimilarCard,
  enrollmentIsland,
}: TrustFirstProps) {
  const { t } = useTranslation('common');
  const chapters = course.chapters ?? [];
  const firstLessonId = chapters[0]?.lessons?.[0]?.id ?? 'l1';

  // Pick the highest-rated featured review for the above-fold spotlight.
  // If reviews exist but no featured array, this is undefined and the
  // featured slot doesn't render.
  const featuredReview = course.reviewSummary?.featured
    ? [...course.reviewSummary.featured].sort((a, b) => b.rating - a.rating)[0]
    : undefined;

  const tabs: TabSpec[] = [
    {
      id: 'outcomes',
      label: t('label.learningOutcomes'),
      show: (course.outcomes?.length ?? 0) > 0,
    },
    { id: 'curriculum', label: t('title.curriculum') },
    { id: 'reviews', label: t('label.allComments'), show: Boolean(course.reviewSummary) },
    {
      id: 'career',
      label: t('label.skillsAndCareer'),
      show: Boolean(course.skills?.length || course.careerOutcomes),
    },
    { id: 'faq', label: t('label.FAQ'), show: (course.faqs?.length ?? 0) > 0 },
    { id: 'resources', label: t('label.documents'), show: (course.resources?.length ?? 0) > 0 },
  ];

  return (
    <CourseLayout
      hero={
        <header className="cd-hero">
          <Breadcrumb
            tenantSlug={tenantSlug}
            categorySlug={course.category}
            categoryName={course.categoryName}
            courseTitle={course.title}
          />
          <CourseTitleBlock course={course} />

          {/* Stats row keeps it brief — instructor card carries the
              trust narrative, not raw numbers. */}
          <div style={{ marginTop: 12, marginBottom: 24 }}>
            <StatRow course={course} show={['rating', 'enrolled']} />
          </div>

          {/* Preview video + instructor side by side — the two
              biggest trust signals on the page. */}
          <div className="cd-two-col" style={{ marginBottom: 24 }}>
            {course.previewVideoUrl ? (
              <PreviewVideoCard
                posterUrl={course.imageUrl}
                href={`/${tenantSlug}/learn/${course.id}/play/${firstLessonId}`}
              />
            ) : (
              <div />
            )}
            <InstructorCard instructor={course.instructor} size="lg" />
          </div>

          {/* Featured review — pulled up from the reviews section.
              The signal: "real people who took this course liked it."
              The trust-first page wants this above the fold. */}
          {featuredReview && (
            <div style={{ marginBottom: 16 }}>
              <ReviewCard review={featuredReview} featured />
            </div>
          )}
        </header>
      }
      main={
        <CourseTabs
          tabs={tabs}
          defaultTabId="outcomes"
          panels={{
            outcomes: (course.outcomes?.length ?? 0) > 0 && (
              <SectionShell title={t('label.whatYouWillLearn')}>
                {/* AI summary above outcomes — Trust-first's default
                    tab is outcomes, so the AI block sits here to
                    frame the takeaways. */}
                {(course.aiHighlights?.length ?? 0) > 0 && (
                  <div style={{ marginBottom: 24 }}>
                    <AiHighlightsBlock highlights={course.aiHighlights} />
                  </div>
                )}
                <OutcomesList outcomes={course.outcomes ?? []} />
              </SectionShell>
            ),
            curriculum: (
              <SectionShell title={t('title.curriculum')}>
                <ChapterTree
                  chapters={chapters}
                  defaultOpen={false}
                  tenantSlug={tenantSlug}
                  courseId={course.id}
                />
              </SectionShell>
            ),
            reviews: course.reviewSummary && (
              <SectionShell title={t('label.allComments')}>
                <RatingHistogram
                  rating={course.ratingAverage}
                  ratingCount={course.ratingCount}
                  distribution={course.reviewSummary.distribution}
                />
                <div style={{ display: 'grid', gap: 12 }}>
                  {course.reviewSummary.featured.map((r, i) => (
                    <ReviewCard key={i} review={r} />
                  ))}
                </div>
              </SectionShell>
            ),
            career: (course.skills?.length || course.careerOutcomes) && (
              <SectionShell title={t('label.skillsAndCareer')}>
                {course.skills && course.skills.length > 0 && (
                  <div style={{ marginBottom: 32 }}>
                    <SkillChips skills={course.skills} />
                  </div>
                )}
                {course.careerOutcomes && <CareerBlock career={course.careerOutcomes} />}
              </SectionShell>
            ),
            faq: (course.faqs?.length ?? 0) > 0 && (
              <SectionShell title={t('title.faq')}>
                <FaqAccordion faqs={course.faqs ?? []} />
              </SectionShell>
            ),
            resources: (course.resources?.length ?? 0) > 0 && (
              <SectionShell title={t('label.documents')}>
                <ResourceList resources={course.resources ?? []} />
              </SectionShell>
            ),
          }}
        />
      }
      sidebar={
        <PriceCard
          course={course}
          tenantSlug={tenantSlug}
          firstLessonId={firstLessonId}
          variant="minimal"
          enrollAction={enrollmentIsland}
        />
      }
      full={
        related.length > 0 && (
          <SectionShell title={t('title.similarLearnings')}>
            <RelatedCourses courses={related} Card={SimilarCard} />
          </SectionShell>
        )
      }
    />
  );
}
